import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import DashboardCard from './DashboardCard';
import {
  formatTry, PROJECT_UNASSIGNED_COLOR, PROJECT_OTHER_COLOR, PROJECT_TOP_N, projectLabel,
} from '../constants';
import { Footnote } from '../styles';

// Proje payı — son 6 ayın onaylı ₺ harcamasının projelere dağılımı (halka). Merkezde
// "projeye bağlı" oranı; ilk 5 proje + "diğer projeler" + projesiz harcama.

const Body = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 12px;
  align-items: center;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Donut = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  margin: 0 auto;
`;

const Center = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const CenterValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
`;

const CenterLabel = styled.div`
  margin-top: 4px;
  font-size: 10px;
  font-weight: 600;
  color: #6B7280;
  text-align: center;
  max-width: 90px;
`;

const Legend = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const Row = styled.li`
  display: grid;
  grid-template-columns: 10px 1fr auto;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  border-radius: 4px;
  padding: 2px 4px;
  &:hover { background: ${({ $clickable }) => ($clickable ? '#F5F8FB' : 'transparent')}; }
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  background: ${({ $color }) => $color};
`;

const Name = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
`;

const Amount = styled.span`
  color: #6B7280;
  font-weight: 700;
  white-space: nowrap;
`;

/// Görünüm modeli (saf — testte doğrudan sınanır).
export function buildShareModel(data, projectColors, t) {
  const items = (data?.items || []).filter((i) => i.windowTotal > 0);
  const projeli = items.filter((i) => i.projectId).sort((a, b) => b.windowTotal - a.windowTotal);
  const projesiz = items.find((i) => !i.projectId);
  const top = projeli.slice(0, PROJECT_TOP_N);
  const digerToplam = projeli.slice(PROJECT_TOP_N).reduce((s, i) => s + i.windowTotal, 0);
  const toplam = items.reduce((s, i) => s + i.windowTotal, 0);

  const dilimler = top.map((i) => ({
    key: i.projectId,
    projectId: i.projectId,
    name: projectLabel(i, t),
    value: i.windowTotal,
    color: projectColors.get(i.projectId) || PROJECT_OTHER_COLOR,
  }));
  if (digerToplam > 0) {
    dilimler.push({ key: 'other', name: t('expenseDashboard.projects.otherProjects'), value: digerToplam, color: PROJECT_OTHER_COLOR });
  }
  if (projesiz) {
    dilimler.push({ key: 'none', name: projectLabel(projesiz, t), value: projesiz.windowTotal, color: PROJECT_UNASSIGNED_COLOR });
  }
  return dilimler.map((d) => ({ ...d, pct: toplam > 0 ? Math.round((d.value * 100) / toplam) : 0 }));
}

export default function ProjectShareCard({ slice, projectColors }) {
  const { t } = useTranslation();
  const history = useHistory();
  const data = slice.data;

  const model = useMemo(() => buildShareModel(data, projectColors, t), [data, projectColors, t]);
  const openProject = (d) => {
    if (d.projectId) history.push(`/all-expenses?projectId=${d.projectId}&projectName=${encodeURIComponent(d.name)}`);
  };

  return (
    <DashboardCard
      cardKey="proje-payi"
      title={t('expenseDashboard.projects.shareTitle')}
      meta={t('expenseDashboard.projects.windowMeta')}
      span={4}
      spanMd={6}
      loading={slice.loading}
      empty={model.length === 0}
      emptyText={t('expenseDashboard.projects.empty')}
    >
      <div>
        <Body>
          <Donut>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={model}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={50}
                  outerRadius={72}
                  paddingAngle={model.length > 1 ? 2 : 0}
                  cornerRadius={4}
                  stroke="none"
                  isAnimationActive={false}
                >
                  {model.map((d) => <Cell key={d.key} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={(value, name) => [formatTry(value), name]} />
              </PieChart>
            </ResponsiveContainer>
            <Center>
              <CenterValue data-testid="assigned-share">
                {data?.assignedSharePct == null ? '—' : `%${data.assignedSharePct}`}
              </CenterValue>
              <CenterLabel>{t('expenseDashboard.projects.assignedLabel')}</CenterLabel>
            </Center>
          </Donut>
          <Legend>
            {model.map((d) => (
              <Row
                key={d.key}
                data-testid={`share-row-${d.key}`}
                $clickable={Boolean(d.projectId)}
                onClick={() => openProject(d)}
                title={d.name}
              >
                <Dot $color={d.color} />
                <Name>{d.name}</Name>
                <Amount>{`%${d.pct} · ${formatTry(d.value)}`}</Amount>
              </Row>
            ))}
          </Legend>
        </Body>
        {data?.nonTryCount > 0 && <Footnote>{t('expenseDashboard.fxExcluded', { count: data.nonTryCount })}</Footnote>}
      </div>
    </DashboardCard>
  );
}
